<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted, onBeforeUnmount, PropType } from 'vue'

// スライドを移動する swipe 量のしきい値(%)
const AMOUNT_OF_SWIPE_THRESHOLD = 0.2
// swipe 開始と判定する移動量しきい値（上下スクロール操作時に意図しない swipe を発生しないようにするための設定）
const ENABLE_SWIPE_AMOUNT = 7
// swipe の進行方向
const IS_NEXT = 1
const IS_PREV = -1

// スタイル用モジュール名
const MODULE_NAME = {
  UNIT: 'l-unit__carousel',
  COLS: 'l-cols__carousel',
  NAVIGATION: 'o-action__carouselNavigation',
  INDICATOR_LIST: 'o-list__carouselIndicator',
  INDICATOR: 'o-selector__carouselIndicator',
  THUMBNAIL: 'o-figure__carouselThumbnail',
} as const

const className = {
  unit: {
    block: MODULE_NAME.UNIT,
    elements: {
      description: `${MODULE_NAME.UNIT}--description`,
      action: `${MODULE_NAME.UNIT}--action`,
    },
  },
  cols: {
    block: MODULE_NAME.COLS,
    elements: {
      col: `${MODULE_NAME.COLS}--col`,
    },
  },
  navigation: {
    block: MODULE_NAME.NAVIGATION,
    elements: {
      items: `${MODULE_NAME.NAVIGATION}--items`,
      item: `${MODULE_NAME.NAVIGATION}--item`,
      trigger: `${MODULE_NAME.NAVIGATION}--trigger`,
    },
  },
  indicatorList: {
    block: MODULE_NAME.INDICATOR_LIST,
    elements: {
      items: `${MODULE_NAME.INDICATOR_LIST}--items`,
      item: `${MODULE_NAME.INDICATOR_LIST}--item`,
    },
  },
  indicator: {
    block: MODULE_NAME.INDICATOR,
    elements: {
      selector: `${MODULE_NAME.INDICATOR}--selector`,
      label: `${MODULE_NAME.INDICATOR}--label`,
    },
  },
  thumbnail: {
    block: MODULE_NAME.THUMBNAIL,
    elements: {
      item: `${MODULE_NAME.THUMBNAIL}--item`,
      image: `${MODULE_NAME.THUMBNAIL}--image`,
      caption: `${MODULE_NAME.THUMBNAIL}--caption`,
    },
  },
} as const

interface DisplaySlide {
  key: string,
  data: unknown,
  originIndex: number,
  isClone: boolean,
}

interface MoveSlideOptions {
  hasAnimation?: boolean,
  interrupt?: boolean,
}

interface Thumbnail {
  image: string,
  label?: string,
}

// インジケータ radio の name をインスタンスごとに一意化するためのカウンタ。
// 同一ページに複数カルーセルがある場合、name 共有だと radio グループが衝突して
// 片方のドット選択でもう片方のチェックが外れるため。
let carouselInstanceCount = 0

export default defineComponent({
  name: 'CarouselDefault',

  props: {
    /**
     * カルーセルに表示するデータ配列
     * 各スライドの中身は scoped slot（#default="{ item, index }"）で描画する
     */
    items: {
      type: Array as PropType<unknown[]>,
      default: () => [],
    },

    /** 初期表示開始位置 */
    selectedIndex: {
      type: Number,
      default: 0,
    },

    /** スライドアニメーション時間(ミリ秒) */
    transitionTimeMillis: {
      type: Number,
      default: 400,
    },

    /** スライドのループ処理を有効にする */
    enableLoop: {
      type: Boolean,
      default: true,
    },

    /** prev, next 制御ボタンを有効にする */
    enableNavigation: {
      type: Boolean,
      default: true,
    },

    /** インジケータ表示を有効にする */
    enableIndicator: {
      type: Boolean,
      default: true,
    },

    /** 自動スライド送りを有効にする */
    enableAutoPlay: {
      type: Boolean,
      default: false,
    },

    /** 自動スライドのインターバル(ミリ秒) */
    autoPlayIntervalMillis: {
      type: Number,
      default: 5000,
    },

    /** サムネイル画像表示有無 */
    hasThumbnails: {
      type: Boolean,
      default: false,
    },

    /** サムネイル表示用の情報 */
    thumbnails: {
      type: Array as PropType<Thumbnail[]>,
      default: () => [],
    },

    /** 表示するスライドアイテム数 */
    numberOfDisplayItems: {
      type: Number,
      default: 1,
    },

    /** 一度にスライドするアイテム数 */
    numberOfSlideItems: {
      type: Number,
      default: 1,
    },

    /**
     * 前側スライドのチラ見せ領域の割合
     * @description viewport 幅に対する 0〜0.5 の値（例: 0.1 で 10%）。0 のとき peek 無し。
     */
    peekPrevRate: {
      type: Number,
      default: 0,
      validator: (value: number) => value >= 0 && value <= 0.5,
    },

    /**
     * 後側スライドのチラ見せ領域の割合
     * @description viewport 幅に対する 0〜0.5 の値（例: 0.1 で 10%）。0 のとき peek 無し。
     */
    peekNextRate: {
      type: Number,
      default: 0,
      validator: (value: number) => value >= 0 && value <= 0.5,
    },

    /** スライド間の gap(px)。各 col の左右に gap/2 ずつ padding として配置される */
    slideGap: {
      type: Number,
      default: 0,
    },

    /** 派生系のカルーセルコンポネで渡す CSS の状態修飾子クラス名 */
    additionalStyleClass: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * ハンバーガーメニューなどv-show(display: none;)で表示の切り替えを行うコンポーネント内に
     * カルーセルがある場合、カルーセルアイテムのwidthが0になってしまう。
     * そのため表示状態を監視して、表示時にwidthを再計算する。
     */
    isParentShown: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // template refs
    const rootEl = ref<HTMLElement | null>(null)
    const trackEl = ref<HTMLElement | null>(null)

    // インジケータ radio の name（インスタンス一意。複数カルーセル間の radio グループ衝突を防ぐ）
    const indicatorName = `carouselIndicator-${++carouselInstanceCount}`

    // reactive state
    /** displaySlides 配列内での現在位置（クローン含む） */
    const domIndex = ref(0)
    /** ドラッグ中の px オフセット（CSS 変数として transform に流す） */
    const dragDeltaPx = ref(0)
    // 初期描画では transition を無効にしておく（マウント直後のアニメ発火を防ぐ）
    const hasAnimation = ref(false)
    const moveSlideIsInProgress = ref(false)

    // non-reactive internal state
    let swipeInitialPosition = 0
    let swipeCurrentPosition: number | null = null
    /** swipe 開始時にスライド幅を 1 回だけ計測（しきい値判定用） */
    let slideWidthAtSwipeStart = 0
    let isDragging = false
    let inProgressAutoPlay = false
    let autoPlayIntervalId: ReturnType<typeof setInterval> | null = null

    // computed
    const itemCount = computed(() => props.items.length)

    const enableSlide = computed(() => itemCount.value > props.numberOfDisplayItems)

    const useLoop = computed(() => enableSlide.value && props.enableLoop)

    /** 先頭クローンのオフセット（ループ時 = numberOfDisplayItems、それ以外 0） */
    const headOffset = computed(() => useLoop.value ? props.numberOfDisplayItems : 0)

    const validSlideIndex = computed(() => (
      props.selectedIndex >= 0 &&
      props.selectedIndex < itemCount.value &&
      enableSlide.value
    ))

    /**
     * 実際に描画するスライド一覧
     * ループ有効時は、cloneNode の代わりに前後にクローン分のデータを並べる
     * （末尾 n 件を先頭へ、先頭 n 件を末尾へ）
     */
    const displaySlides = computed<DisplaySlide[]>(() => {
      const list = props.items
      const n = props.numberOfDisplayItems

      const originals: DisplaySlide[] = list.map((data, i) => ({
        key: `origin-${i}`,
        data,
        originIndex: i,
        isClone: false,
      }))

      if (!enableSlide.value || !props.enableLoop) {
        return originals
      }

      const headClones: DisplaySlide[] = list.slice(list.length - n).map((data, i) => ({
        key: `head-${i}`,
        data,
        originIndex: list.length - n + i,
        isClone: true,
      }))

      const tailClones: DisplaySlide[] = list.slice(0, n).map((data, i) => ({
        key: `tail-${i}`,
        data,
        originIndex: i,
        isClone: true,
      }))

      return [...headClones, ...originals, ...tailClones]
    })

    /** 現在表示している original index（インジケータ active 判定用） */
    const slideIndex = computed(() => {
      const slide = displaySlides.value[domIndex.value]
      return slide ? slide.originIndex : 0
    })

    // nav / indicator は「スライド可能なとき(enableSlide)」だけ表示する共通ルール
    const showNavigation = computed(() => props.enableNavigation && enableSlide.value)
    const showIndicator = computed(() => props.enableIndicator && enableSlide.value)

    /**
     * 非ループ時に到達できる最後のストップ（左端の original index）。
     * ストライド(numberOfSlideItems)の倍数で、最終アイテムを含む最小位置に止める。
     * 例: items5 / display3 / slide3 → 3（= [3,4,空] で停止。空スロットへ進ませない）。
     * display1 のときは itemCount - 1（最終アイテム）に退化する。
     */
    const lastStopIndex = computed(() => {
      const remaining = itemCount.value - props.numberOfDisplayItems
      if (remaining <= 0) return 0
      const stride = Math.max(1, props.numberOfSlideItems)
      // 上限は最終アイテム（itemCount-1）。stride > display の誤設定で範囲外を指さないようキャップ。
      return Math.min(itemCount.value - 1, Math.ceil(remaining / stride) * stride)
    })

    const disabledPrevNavigation = computed(() => (
      (!props.enableLoop && slideIndex.value === 0) || !enableSlide.value
    ))

    const disabledNextNavigation = computed(() => (
      (!props.enableLoop && slideIndex.value >= lastStopIndex.value) || !enableSlide.value
    ))

    const trackStyle = computed<Record<string, string | number>>(() => ({
      '--display-count': displaySlides.value.length === 0 ? 1 : props.numberOfDisplayItems,
      '--total-slides': displaySlides.value.length === 0 ? 1 : displaySlides.value.length,
      '--dom-index': domIndex.value,
      '--drag-delta': `${dragDeltaPx.value}px`,
      '--transition-time': `${props.transitionTimeMillis}ms`,
      // peek 値は 0..0.5 にクランプ（合計が 1 に達すると slide 占有が 0 になり破綻するため）
      '--peek-prev': Math.max(0, Math.min(props.peekPrevRate, 0.5)),
      '--peek-next': Math.max(0, Math.min(props.peekNextRate, 0.5)),
      '--slide-gap': `${props.slideGap}px`,
    }))

    // methods
    const transitionEnd = () => {
      moveSlideIsInProgress.value = false
    }

    /**
     * スライドの transition 完了を待つ Promise を返す。
     * - skipAnimation=true: アニメーション無しなので即座に resolve（moveSlideIsInProgress も解除）
     * - skipAnimation=false: transitionEnd() による moveSlideIsInProgress の false 化を待って resolve。
     *   transform が変化せず transitionend が発火しないケースに備え、
     *   transitionTimeMillis + 50ms のタイマーをフォールバックとして併用する。
     * @param skipAnimation アニメーションをスキップ（即時 resolve）するか
     */
    const awaitTransition = (skipAnimation: boolean): Promise<void> => {
      return new Promise(resolve => {
        if (skipAnimation) {
          moveSlideIsInProgress.value = false
          resolve()
          return
        }

        let resolved = false
        let stop: (() => void) | null = null
        let timer: ReturnType<typeof setTimeout> | null = null

        const finish = () => {
          if (resolved) return
          resolved = true
          stop?.()
          if (timer) clearTimeout(timer)
          moveSlideIsInProgress.value = false
          resolve()
        }

        // transitionEnd() による moveSlideIsInProgress の解除を待つ
        stop = watch(moveSlideIsInProgress, (newValue, oldValue) => {
          if (newValue === false && oldValue === true) finish()
        })

        // transform が変化せず transitionend が発火しないケースに備えたフォールバック
        timer = setTimeout(finish, props.transitionTimeMillis + 50)
      })
    }

    /**
     * 指定された displaySlides index へ移動する。
     * クローン位置に着地した場合はアニメーション後に等価な原本位置にスナップする。
     *
     * @param rawTargetIndex displaySlides 配列上のインデックス
     */
    const moveSlide = async (
      rawTargetIndex: number,
      { hasAnimation: shouldAnimate = true, interrupt = false }: MoveSlideOptions = {},
    ): Promise<void> => {
      if (moveSlideIsInProgress.value && !interrupt) return

      const total = displaySlides.value.length
      if (total === 0) return

      // 範囲外 → 非ループ時はクランプ、ループ時は無視
      // 非ループは total-1 ではなく lastStopIndex で止める（多列時に末尾の空スロットへ進ませない / swipe 越境も防ぐ）
      let targetIndex = rawTargetIndex
      if (!useLoop.value) {
        targetIndex = Math.max(0, Math.min(targetIndex, lastStopIndex.value))
      } else if (targetIndex < 0 || targetIndex >= total) {
        return
      }

      const slide = displaySlides.value[targetIndex]
      if (!slide) return

      moveSlideIsInProgress.value = true
      hasAnimation.value = shouldAnimate
      domIndex.value = targetIndex
      await awaitTransition(!shouldAnimate)

      if (slide.isClone) {
        // クローン位置 → 等価な原本位置にスナップ（アニメーション無し）
        hasAnimation.value = false
        domIndex.value = targetIndex < headOffset.value
          ? targetIndex + itemCount.value     // 先頭クローン → 末尾側の原本
          : targetIndex - itemCount.value     // 末尾クローン → 先頭側の原本
      }
    }

    const stopAutoPlay = () => {
      if (!inProgressAutoPlay) return
      if (autoPlayIntervalId) clearInterval(autoPlayIntervalId)
      autoPlayIntervalId = null
      inProgressAutoPlay = false
    }

    const startAutoPlay = () => {
      if (!props.enableAutoPlay) return
      if (inProgressAutoPlay) return

      inProgressAutoPlay = true

      autoPlayIntervalId = setInterval(() => {
        if (!enableSlide.value) return
        moveSlide(domIndex.value + props.numberOfSlideItems)
      }, props.autoPlayIntervalMillis)
    }

    const prev = async () => {
      if (disabledPrevNavigation.value) return

      stopAutoPlay()
      await moveSlide(domIndex.value - props.numberOfSlideItems)
      startAutoPlay()
    }

    const next = async () => {
      if (disabledNextNavigation.value) return

      stopAutoPlay()
      await moveSlide(domIndex.value + props.numberOfSlideItems)
      startAutoPlay()
    }

    const swipeStart = (e: TouchEvent | MouseEvent, isTouchEvent = false) => {
      stopAutoPlay()

      if (moveSlideIsInProgress.value) return

      if (!isTouchEvent) {
        isDragging = true
        // ネイティブの画像/テキストドラッグを抑止し、mousemove 追従を可能にする
        // （preventDefault しても click は発火するのでリンク/ボタンは動作する）
        e.preventDefault()
      }

      // しきい値判定用にスライド 1 枚の幅を 1 度だけ取得（reactive 不要）
      const firstCol = trackEl.value?.firstElementChild as HTMLElement | null
      slideWidthAtSwipeStart = firstCol?.getBoundingClientRect().width ?? 0

      hasAnimation.value = false
      swipeInitialPosition = isTouchEvent
        ? (e as TouchEvent).changedTouches[0].pageX
        : (e as MouseEvent).pageX
    }

    const swipe = (e: TouchEvent | MouseEvent, isTouchEvent = false) => {
      if (moveSlideIsInProgress.value) return
      if (!isTouchEvent && !isDragging) return

      swipeCurrentPosition = isTouchEvent
        ? (e as TouchEvent).changedTouches[0].pageX
        : (e as MouseEvent).pageX

      const delta = swipeCurrentPosition - swipeInitialPosition

      if (Math.abs(delta) > ENABLE_SWIPE_AMOUNT) {
        // CSS 変数 --drag-delta に px 値を流して、トラックの transform を追従させる
        dragDeltaPx.value = delta
        e.preventDefault()
      }
    }

    const swipeEnd = (_: TouchEvent | MouseEvent, isTouchEvent = false) => {
      startAutoPlay()

      if (swipeCurrentPosition === null) {
        // クリック等で move が無かった場合。mouse はここでドラッグ状態を解除する。
        // （解除しないと次の mousemove で、押下していないのにスワイプが始まってしまう）
        if (!isTouchEvent) isDragging = false
        moveSlideIsInProgress.value = false
        return
      }

      if (!isTouchEvent && !isDragging) return

      const delta = swipeCurrentPosition - swipeInitialPosition
      const threshold = slideWidthAtSwipeStart * AMOUNT_OF_SWIPE_THRESHOLD
      const shouldAdvance = enableSlide.value && Math.abs(delta) > threshold

      // 指 / カーソルを左に動かした(delta < 0) → 次へ
      const direction = delta < 0 ? IS_NEXT : IS_PREV
      const targetIndex = shouldAdvance
        ? domIndex.value + props.numberOfSlideItems * direction
        : domIndex.value

      // ドラッグオフセットを 0 に戻し、コミット位置へアニメーションで遷移
      dragDeltaPx.value = 0
      moveSlide(targetIndex)

      if (!isTouchEvent) {
        isDragging = false
      }
      swipeCurrentPosition = null
    }

    /** original index を受け取り、対応する domIndex に直す */
    const toDomIndex = (originalIndex: number): number => headOffset.value + originalIndex

    /** props から初期 domIndex を決定する */
    const computeInitialDomIndex = (): number => (
      toDomIndex(validSlideIndex.value ? props.selectedIndex : 0)
    )

    // 初期 domIndex セット
    domIndex.value = computeInitialDomIndex()

    // watch
    watch(() => props.selectedIndex, () => {
      if (!validSlideIndex.value || itemCount.value === 0) return
      moveSlide(toDomIndex(props.selectedIndex), { interrupt: true })
    })

    // items が変わったら先頭にリセット（アニメ無しでスナップ）
    watch(() => props.items, () => {
      hasAnimation.value = false
      domIndex.value = computeInitialDomIndex()
      nextTick(() => { hasAnimation.value = true })
    })

    // 表示数が変わると（PC/SP 切替など）headOffset・クローン構成が変わり、
    // 既存 domIndex が指す位置の意味がズレる。初期位置へ取り直す（アニメ無し）
    watch(() => props.numberOfDisplayItems, () => {
      hasAnimation.value = false
      domIndex.value = computeInitialDomIndex()
      nextTick(() => { hasAnimation.value = true })
    })

    // lifecycle
    onMounted(() => {
      // 初期表示が描画された後、以降のスライド操作はアニメーションさせる
      nextTick(() => { hasAnimation.value = true })
      startAutoPlay()
    })

    onBeforeUnmount(() => {
      stopAutoPlay()
    })

    // サムネイルインジケータは本コンポーネント固有の表示機能
    const existsThumbnail = (index: number): boolean => {
      return props.hasThumbnails && !!props.thumbnails[index]
    }

    return {
      // constants
      className,
      indicatorName,

      // template refs
      rootEl,
      trackEl,

      // carousel state / computed
      displaySlides,
      domIndex,
      dragDeltaPx,
      slideIndex,
      hasAnimation,
      enableSlide,
      showNavigation,
      showIndicator,
      disabledPrevNavigation,
      disabledNextNavigation,
      trackStyle,

      // methods
      moveSlide,
      toDomIndex,
      prev,
      next,
      swipeStart,
      swipe,
      swipeEnd,
      transitionEnd,
      existsThumbnail,
    }
  },
})
</script>

<template>
  <div
    ref="rootEl"
    :class="[className.unit.block, additionalStyleClass]">
    <div :class="className.unit.elements.description">
      <!-- カルーセル本体 -->
      <div
        ref="trackEl"
        :class="[className.cols.block, additionalStyleClass]"
        :data-animation="hasAnimation"
        :style="trackStyle"
        @touchstart="swipeStart($event, true)"
        @touchmove="swipe($event, true)"
        @touchend="swipeEnd($event, true)"
        @mousedown="swipeStart($event, false)"
        @mousemove="swipe($event, false)"
        @mouseup="swipeEnd($event, false)"
        @mouseleave="swipeEnd($event, false)"
        @transitionend="transitionEnd">
        <!-- スライドの枠は内部で管理し、中身は scoped slot で呼び出し側が描画する -->
        <div
          v-for="slide in displaySlides"
          :key="slide.key"
          :class="[className.cols.elements.col, { 'is-clone': slide.isClone }]">
          <slot
            :item="slide.data"
            :index="slide.originIndex" />
        </div>
      <!-- /.l-cols__carousel --></div>
    <!-- /.l-unit__carousel description --></div>

    <!-- prev / next コントローラ -->
    <template v-if="showNavigation">
      <!-- prev -->
      <div :class="[className.unit.elements.action, 'is-navigation', 'is-prev']">
        <div :class="[className.navigation.block, 'is-prev']">
          <button
            :class="className.navigation.elements.item"
            :disabled="disabledPrevNavigation"
            @click.prevent="prev">
            前へ
          </button>
        <!-- /.o-action__carouselNavigation --></div>
      <!-- /.l-unit__carousel action --></div>

      <!-- next -->
      <div :class="[className.unit.elements.action, 'is-navigation', 'is-next']">
        <div :class="[className.navigation.block, 'is-next']">
          <button
            :class="className.navigation.elements.item"
            :disabled="disabledNextNavigation"
            @click.prevent="next">
            次へ
          </button>
        <!-- /.o-action__carouselNavigation --></div>
      <!-- /.l-unit__carousel action --></div>
    </template>

    <!-- インジケータ -->
    <div
      v-if="showIndicator"
      :class="[
        className.unit.elements.action,
        hasThumbnails ? 'is-thumbnail' : 'is-indicator',
      ]">
      <div
        :class="[
          className.indicatorList.block,
          { 'is-thumbnail': hasThumbnails },
        ]">
        <ul :class="className.indicatorList.elements.items">
          <li
            v-for="(_, index) in items"
            :key="index"
            :class="className.indicatorList.elements.item"
            :data-active="index === slideIndex">
            <!-- サムネイルインジケータ -->
            <div
              v-if="existsThumbnail(index)"
              :class="className.thumbnail.block"
              :data-active="index === slideIndex"
              @click.prevent="index !== slideIndex && moveSlide(toDomIndex(index), { interrupt: true })">
              <span :class="className.thumbnail.elements.item">
                <img
                  :class="className.thumbnail.elements.image"
                  :src="thumbnails[index].image"
                  loading="lazy"
                  data-is-liquid="true"
                  data-is-copyblock="true"
                  alt="">
              </span>

              <!-- サムネイルキャプション -->
              <p
                v-if="thumbnails[index].label"
                :class="className.thumbnail.elements.caption">
                {{ thumbnails[index].label }}
              </p>
            <!-- /.o-figure__carouselThumbnail --></div>

            <!-- ドットインジケータ -->
            <div
              v-else
              :class="className.indicator.block"
              :data-active="index === slideIndex">
              <label
                :class="className.indicator.elements.label"
                @click.prevent="index !== slideIndex && moveSlide(toDomIndex(index), { interrupt: true })">
                <input
                  :class="className.indicator.elements.selector"
                  type="radio"
                  :name="indicatorName"
                  :value="index"
                  :checked="index === slideIndex">
              </label>
            <!-- /.o-selector__carouselIndicator --></div>
          </li>
        </ul>
      <!-- /.o-list__carouselIndicator --></div>
    <!-- /.l-unit__carousel action --></div>
  <!-- /.l-unit__carousel --></div>
</template>

<style lang="sass" scoped>
.l-unit
  &__carousel
    $root: &

    position: relative
    margin-top: 1.5em

    &:first-child
      margin-top: 0

    & &--description
      position: relative
      overflow: hidden

    & &--action
      // prev / next コントローラ
      &.is-navigation
        height: 64px
        width:  44px
        position: absolute
        top: 50%
        transform: translateY(-50%)
        z-index: 1

        &.is-prev
          left: 0

        &.is-next
          right: 0

      // インジケータ
      &.is-indicator
        position: absolute
        left: 0
        right: 0
        bottom: 1em
        margin: auto
        z-index: 1

      // サムネイル
      &.is-thumbnail
        margin-top: 1em
        padding: 0 0.5em

.l-cols
  &__carousel
    $root: &
    display: flex
    position: relative
    box-sizing: content-box

    // カルーセル本体の幅・位置はすべて CSS 変数で表現
    // スライド 1 枚は viewport × (1 - peek-prev - peek-next) / display-count の幅を占める
    // トラック総幅 = 1 スライドの幅 × total-slides
    width: calc(100% * var(--total-slides, 1) * (1 - var(--peek-prev, 0) - var(--peek-next, 0)) / var(--display-count, 1))
    // transform:
    //   - display-index ごとに 1 スライド幅（トラック比 100%/total-slides）左へ移動
    //   - peek-prev のぶん右へずらして、現在スライドの左隣を peek 領域に見せる
    //     ( peek-prev × viewport を トラック比で表すと
    //       peek-prev × display-count / (total-slides × (1 - peek-prev - peek-next)) )
    //   - ドラッグ中のオフセット
    transform: translateX(calc(var(--dom-index, 0) * -100% / var(--total-slides, 1) + var(--peek-prev, 0) * var(--display-count, 1) * 100% / (var(--total-slides, 1) * (1 - var(--peek-prev, 0) - var(--peek-next, 0))) + var(--drag-delta, 0px)))

    &[data-animation="true"]
      transition: transform var(--transition-time, 400ms) ease

    &[data-animation="false"]
      transition: none

    & &--col
      // 各スライドはトラック幅の 1/total-slides = viewport × slide-occupancy / display-count ぶんの幅
      flex: 0 0 calc(100% / var(--total-slides, 1))
      // gap を border-box の内側 padding(左右 gap/2 ずつ)で表現
      // 隣接 col 同士の padding 合算で full gap になる
      box-sizing: border-box
      padding: 0 calc(var(--slide-gap, 0px) / 2)

.o-list
  &__carouselIndicator
    $root: &

    margin-top: 0.5em

    &:first-child
      margin-top: 0

    & &--items
      display: flex
      justify-content: center

    & &--item
      margin-left: 0.5em

      &:first-child
        margin-left: 0

    &.is-thumbnail
      $cols: 5  // サムネイルの横最大個数
      $margin-size: 20  // サムネイル用の相対値計算用マージン基準値

      #{$root}--items
        flex-wrap: wrap

      #{$root}--item
        $col-width: math.div(($global-contents-width-pc - $margin-size * ($cols - 1)), $cols)
        width: getRelativeValue($col-width, $global-contents-width-pc)
        margin: changeSizePx2Em(20) 0 0 getRelativeValue($margin-size, $global-contents-width-pc)

        &:nth-child(#{$cols}n+1)
          margin-left: 0

        &:nth-child(-n+#{$cols})
          margin-top: 0

      +breakpoint
        $cols: 4  // サムネイルの横最大個数
        $col-width: math.div(($global-contents-width-phablet - $margin-size * ($cols - 1)), $cols)
        #{$root}--item
          width: getRelativeValue($col-width, $global-contents-width-phablet)

      +breakpoint($max-media: sp)
        $cols: 3  // サムネイルの横最大個数
        $col-width: math.div(($global-contents-width-sp - $margin-size * ($cols - 1)), $cols)
        #{$root}--item
          width: getRelativeValue($col-width, $global-contents-width-sp)

.o-action
  &__carouselNavigation
    $root: &
    $padding: 10

    width: 100%
    height: 100%

    & &--item
      width: 100%
      height: 100%
      position: relative
      display: block
      overflow: hidden
      color: rgba($font-white, 0)
      border: none

      &::before,
      &::after
        position: absolute
        top: 50%
        left: 50%

    &.is-prev
      #{$root}--item
        border-radius: 0 $border-radius $border-radius 0

        &::before
          +iconArrow(left, $border-xx-dark-gray, 24px)
          // [注] スタイル記載位置
          // mixin で include している iconArrow で transform を使用しており、
          // アイコンの位置調整に transform を使うには、mixin の include のあとに定義してカスケードする必要がある。
          // prev / next ともに同じ値だが、& &--item に定義すると、iconArrow の mixin のスタイル詳細度に勝てないため、冗長だが個別定義とする
          transform: scaleX(0.8) rotate(-45deg) translate(-50%, -50%)

    &.is-next
      #{$root}--item
        border-radius: $border-radius 0 0 $border-radius

        &::after
          +iconArrow(right, $border-xx-dark-gray, 24px)
          transform: scaleX(0.8) rotate(-45deg) translate(-50%, -50%)

.o-selector
  &__carouselIndicator
    $root: &

    & &--label
      width: changeSizePx2Em(6)
      height: changeSizePx2Em(6)
      display: block
      position: relative
      color: rgba($font-white, 0)
      background-color: rgba($bg-light-gray, 1)
      border-radius: 50%

    & &--selector
      width: 0
      height: 0
      position: absolute
      top: 0
      left: 0
      border: none

    & &--caption
      font-size: $font-x-small
      margin-top: 0.5em

    &[data-active="true"]
      #{$root}--label
        background-color: rgba($bg-black, 1)

    &.is-thumbnail
      #{$root}--label
        width: auto
        height: auto
        border: solid 1px rgba($border-white, 0)
        background-color: transparent
        border-radius: 0

      &[data-active="true"]
        #{$root}--label
          border: solid 1px $border-black
          background-color: transparent

.o-figure
  &__carouselThumbnail
    $root: &
    margin: 30px 0
    padding-bottom: 10px
    border-bottom: 2px solid rgba($border-white, 0)

    &:first-child
      margin-top: 0

    &:last-child
      margin-bottom: 0

    &[data-active="true"]
      border-bottom: 2px solid $border-orange

    & &--item
      $image-width: 320
      $image-height: 174
      display: block
      position: relative

      &::before
        width: 0
        height: 0
        display: block
        padding-top: imageAspectVertical($image-width, $image-height)

    & &--image
      display: block

      &[data-is-liquid="true"]
        width: 100%
        height: auto
        vertical-align: bottom

    & &--caption
      display: block
      font-size: $font-small
      margin-top: 0.25em

      &:first-child
        margin-top: 0
</style>
